import { useQuery } from '@tanstack/react-query';
import { Button, Pill, SkeletonSet, TableCard, type Column } from '@cwos/ui-admin';
import { useNav } from '../navigation.js';
import { usePermissions } from '../permissions.js';
import { LoadFailure } from '../LoadFailure.js';
import {
  AGENTS_APPROVE_KEY,
  AGENTS_CONFIGURE_KEY,
  AGENTS_DEPLOY_KEY,
  agentAuthority,
  authorityOpen,
  readinessItems,
  readinessLabel,
  readinessPill,
} from './consentLogic.js';
import { listPhase } from '../listState.js';
import { ListState } from '../ListStates.js';
import { agents, type AgentSummary } from './agentsClient.js';
import { AgentApprovals } from './AgentApprovals.js';
import {
  isLive,
  lastRunSentence,
  statusLabel,
  statusPill,
  triggerSentence,
} from './agentsLogic.js';

/**
 * INTELLIGENCE → AGENTS. What is operating in your organization.
 *
 * ===========================================================================
 * WHY THIS SCREEN EXISTS AT ALL
 * ===========================================================================
 * The agent runtime has been durable, wired and running a cron sweep every
 * minute of every day for some time. Nobody inside a tenant could see it. A
 * customer whose agent misbehaved had to telephone the vendor, and a customer
 * who wanted to know what one had done had no way to ask. A feature is not
 * complete because the backend exists.
 *
 * ===========================================================================
 * WHAT THIS LIST DELIBERATELY DOES NOT SHOW
 * ===========================================================================
 * THE PERMISSION PROFILE. It is on the wire — the summary carries it — and it
 * is not a column here, because a column of permission keys beside a row that
 * says "Running" reads as a capability list, and it is not one. An agent's
 * authority is decided when it FIRES, from the live catalog, and a deployer
 * who lost one key yesterday refuses every run today with the profile
 * unchanged. The only place this app states what an agent may do is the detail
 * page, from a verdict re-derived at read time. Getting that answer costs a
 * click, and the click is the point.
 *
 * A COUNT BADGE. Same rule as Insights: a number beside a heading is a
 * standing instruction to make it go down, and an agent's run count is
 * something to know rather than something to clear.
 *
 * ===========================================================================
 * THE FOUR STATES
 * ===========================================================================
 * loading  a skeleton.
 * refused  `Refused`, with no retry. Under this codebase's FORCE RLS model a
 *          403 is an ANSWER, and rendering it as "no agents are deployed"
 *          would tell a customer their organization is running nothing while
 *          something runs every minute. That is the exact defect this
 *          programme has found six times in the backend, and it would be
 *          worse here because this screen is where somebody would go to check.
 * failed   `LoadFailure` with a retry and the sentence that says the panel is
 *          not empty.
 * empty    a measured zero, in its own words. The read succeeded and there
 *          are none.
 */

/** The reading permission this screen's server guard is argued from. */
export const AGENTS_READ_KEY = 'agents:read';

/** The sentence `RequirePermission` shows when the key is missing. */
export const AGENTS_IS =
  'Agents is where you see the automated workers operating inside your organization — what ' +
  'each one is for, what it would be allowed to do the next time it runs, everything it has ' +
  'done, and the controls to pause or stop it.';

export function Agents() {
  const navigate = useNav();
  /**
   * THE WAY IN TO SETTING ONE UP, and it is a DIFFERENT KEY from this screen's.
   *
   * `agents:read` opens this list; `agents:configure` is the governance
   * authority the set-up screen is gated on, carried by org_admin since the
   * owner directive of 2026-10-07. The button is `can()`-shaped rather than `has()`-shaped because the door it
   * leads to refuses a program-scoped grant outright — an agent is an
   * organization-level actor and `agent-runtime/routes.ts:guard` says so — so
   * offering the control to a narrowed holder would be offering a screen that
   * can only refuse them.
   */
  const permissions = usePermissions();
  const configure = agentAuthority(permissions, AGENTS_CONFIGURE_KEY);
  const mayConfigure = authorityOpen(configure);

  const list = useQuery({
    queryKey: ['agents', 'list'],
    queryFn: () => agents.list(),
  });

  /** Enablement + consent, readable on `agents:read` (the same key as this page). */
  const standing = useQuery({
    queryKey: ['agents', 'consent'],
    queryFn: () => agents.consent(),
  });

  const readiness = readinessItems(standing.data ?? null, {
    configure,
    deploy: agentAuthority(permissions, AGENTS_DEPLOY_KEY),
    approve: agentAuthority(permissions, AGENTS_APPROVE_KEY),
  });

  const items = list.data ?? [];

  const columns: Column<AgentSummary>[] = [
    {
      key: 'agent',
      header: 'Agent',
      render: (row) => (
        <div className="rn-col" style={{ gap: 'var(--space-8)' }}>
          <span className="rn-strong">{row.name}</span>
          <span className="rn-muted">{row.objective}</span>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'State',
      render: (row) => (
        <div className="rn-col" style={{ gap: 'var(--space-8)' }}>
          <Pill variant={statusPill(row.status)}>{statusLabel(row.status)}</Pill>
          {/* THE KILL SWITCH IS TWO FIELDS. A deployment can read ACTIVE and
              be switched off, and a screen showing only the status would call
              it Running while every fire is refused. */}
          {row.status === 'ACTIVE' && !row.enabled ? (
            <span className="rn-muted">Switched off — it will not run</span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'trigger',
      header: 'Runs when',
      render: (row) => (
        <span className="rn-muted">
          {row.triggers.length === 0
            ? 'Nothing starts it'
            : row.triggers.map((t) => triggerSentence(t)).join(' · ')}
        </span>
      ),
    },
    {
      key: 'lastRun',
      header: 'Last run',
      render: (row) => (
        <div className="rn-col" style={{ gap: 'var(--space-8)' }}>
          <span>{lastRunSentence(row)}</span>
          {row.lastRun?.startedAt ? (
            <time className="rn-muted" dateTime={new Date(row.lastRun.startedAt).toISOString()}>
              {new Date(row.lastRun.startedAt).toLocaleString()}
            </time>
          ) : null}
        </div>
      ),
    },
  ];

  const live = items.filter((agent) => isLive(agent)).length;

  return (
    <div className="rn-col" style={{ gap: 'var(--space-16)' }}>
      <header className="rn-col" style={{ gap: 'var(--space-8)' }}>
        <h1>Agents</h1>
        <p className="rn-muted">
          Automated workers operating inside your organization. What each one may actually do is
          decided when it runs — open one to see the answer as it stands right now.
        </p>
        {mayConfigure ? (
          <div>
            <Button variant="primary" onClick={() => navigate('/agents/deploy')}>
              Set up an agent
            </Button>
          </div>
        ) : null}
      </header>

      {/* READINESS: every prerequisite, met or not, and who can resolve each —
          so nobody fixes one gap only to discover the next. */}
      <section className="rn-card" data-testid="agent-readiness">
        <h2 className="rn-card-title">What an agent needs to run here</h2>
        {standing.isLoading ? <SkeletonSet surface="table" rows={2} /> : null}
        {standing.isError ? (
          <LoadFailure
            error={standing.error}
            message="We could not read whether Phibian has enabled agents here or whether your organization has consented. This is not a statement that either is missing."
            refusedHeading="You may not see this organization’s agent standing"
            onRetry={() => void standing.refetch()}
          />
        ) : null}
        <ul className="rn-list">
          {readiness.map((item) => (
            <li
              key={item.id}
              className="rn-col"
              style={{ gap: 'var(--space-8)' }}
              data-readiness={item.id}
              data-state={item.state}
            >
              <div className="rn-row" style={{ gap: 'var(--space-12)', alignItems: 'center' }}>
                <Pill variant={readinessPill(item.state)}>{readinessLabel(item.state)}</Pill>
                <span className="rn-strong">{item.label}</span>
              </div>
              <span>{item.detail}</span>
              {item.resolvedBy ? (
                <span className="rn-muted">Who can resolve it: {item.resolvedBy}</span>
              ) : null}
            </li>
          ))}
        </ul>
      </section>

      <AgentApprovals />

      <ListState
        phase={listPhase(list, items.length)}
        surface="table"
        rows={4}
        failedMessage="We could not load your agents. This is not a statement that you have none."
        onRetry={() => void list.refetch()}
        empty={
          <p className="rn-muted">
            No agents are deployed in your organization. Nothing is running on your records.
          </p>
        }
      >
        <>
          <p className="rn-muted">
            {live === items.length
              ? `${items.length} deployed, all of them able to run.`
              : `${items.length} deployed · ${live} able to run.`}
          </p>
          <TableCard
            columns={columns}
            rows={items}
            rowKey={(row) => row.id}
            onRowOpen={(row) => navigate(`/agents/${row.id}`)}
            caption="Agents deployed in your organization"
          />
        </>
      </ListState>
    </div>
  );
}
